import {
    MakeLogicType,
    actions,
    afterMount,
    beforeUnmount,
    connect,
    kea,
    listeners,
    path,
    reducers,
    selectors,
} from 'kea'
import { loaders } from 'kea-loaders'
import { router, urlToAction } from 'kea-router'
import posthog from 'posthog-js'

import api from 'lib/api'
import { lemonToast } from 'lib/lemon-ui/LemonToast/LemonToast'
import { deleteWithUndo } from 'lib/utils/deleteWithUndo'
import { createFuse, Fuse } from 'lib/utils/fuseSearch'
import { databaseTableListLogic } from 'scenes/data-management/database/databaseTableListLogic'

import { DatabaseSchemaDataWarehouseTable } from '~/queries/schema/schema-general'
import {
    DataWarehouseViewLink,
    ExternalDataJobStatus,
    ExternalDataSchemaStatus,
    ExternalDataSource,
    ExternalDataSourceSchema,
} from '~/types'

import type { PaginatedResponse } from '../../../../../frontend/src/lib/api'
import type {
    DatabaseSchemaQueryResponse,
    SourceConfig,
} from '../../../../../frontend/src/queries/schema/schema-general'
import type { ExternalDataSourceRevenueAnalyticsConfig } from '../../../../../frontend/src/types'
import { availableSourcesLogic } from '../../scenes/NewSourceScene/availableSourcesLogic'
import { joinsLogic } from './joinsLogic'
import { sourcesDataLogic } from './sourcesDataLogic'

const REFRESH_INTERVAL = 10000

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface sourceManagementLogicValues {
    availableSources: Record<string, SourceConfig> | null // availableSourcesLogic
    dataWarehouseTables: DatabaseSchemaDataWarehouseTable[] // databaseTableListLogic
    database: Required<DatabaseSchemaQueryResponse> | null // databaseTableListLogic
    dataWarehouseSources: PaginatedResponse<ExternalDataSource> | null // sourcesDataLogic
    dataWarehouseSourcesLoading: boolean // sourcesDataLogic
    filteredManagedSources: ExternalDataSource[]
    filteredSelfManagedTables: DatabaseSchemaDataWarehouseTable[]
    hasZendeskSource: boolean
    managedSearchTerm: string
    managedSources: ExternalDataSource[]
    managedSourcesFuse: Fuse<ExternalDataSource>
    schemaReloadingById: Record<string, boolean>
    schemas: null
    schemasLoading: boolean
    searchTerm: string
    selfManagedTables: DatabaseSchemaDataWarehouseTable[]
    sourceReloadingById: Record<string, boolean>
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface sourceManagementLogicActions {
    loadDatabase: (
        args_0?:
            | {
                  force?: boolean
              }
            | undefined
    ) => {
        force?: boolean
    } // databaseTableListLogic
    loadJoins: () => any // joinsLogic
    loadSources: () => {
        value: true
    } // sourcesDataLogic
    loadSourcesFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    } // sourcesDataLogic
    loadSourcesSuccess: (
        dataWarehouseSources:
            | PaginatedResponse<ExternalDataSource>
            | {
                  count: number
                  next: null
                  previous: null
                  results: never[]
              },
        payload?:
            | {
                  value: true
              }
            | undefined
    ) => {
        dataWarehouseSources:
            | PaginatedResponse<ExternalDataSource>
            | {
                  count: number
                  next: null
                  previous: null
                  results: never[]
              }
        payload?: {
            value: true
        }
    } // sourcesDataLogic
    updateSource: (source: ExternalDataSource) => ExternalDataSource // sourcesDataLogic
    updateSourceRevenueAnalyticsConfig: (args_0: {
        config: Partial<ExternalDataSourceRevenueAnalyticsConfig>
        source: ExternalDataSource
    }) => {
        config: Partial<ExternalDataSourceRevenueAnalyticsConfig>
        source: ExternalDataSource
    } // sourcesDataLogic
    deleteJoin: (join: DataWarehouseViewLink) => {
        join: DataWarehouseViewLink
    }
    deleteSelfManagedTable: (tableId: string) => {
        tableId: string
    }
    deleteSource: (source: ExternalDataSource) => {
        source: ExternalDataSource
    }
    refreshSelfManagedTableSchema: (tableId: string) => {
        tableId: string
    }
    reloadSource: (source: ExternalDataSource) => {
        source: ExternalDataSource
    }
    schemaLoadingFinished: (schema: ExternalDataSourceSchema) => {
        schema: ExternalDataSourceSchema
    }
    setManagedSearchTerm: (managedSearchTerm: string) => {
        managedSearchTerm: string
    }
    setSearchTerm: (searchTerm: string) => {
        searchTerm: string
    }
    sourceLoadingFinished: (source: ExternalDataSource) => {
        source: ExternalDataSource
    }
    updateSchema: (schema: ExternalDataSourceSchema) => ExternalDataSourceSchema
    updateSchemaFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    updateSchemaSuccess: (
        schemas: null,
        payload?: ExternalDataSourceSchema
    ) => {
        schemas: null
        payload?: ExternalDataSourceSchema
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface sourceManagementLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        selfManagedTables: (
            dataWarehouseTables: DatabaseSchemaDataWarehouseTable[]
        ) => DatabaseSchemaDataWarehouseTable[]
        filteredSelfManagedTables: (
            selfManagedTables: DatabaseSchemaDataWarehouseTable[],
            searchTerm: string
        ) => DatabaseSchemaDataWarehouseTable[]
        managedSources: (dataWarehouseSources: PaginatedResponse<ExternalDataSource> | null) => ExternalDataSource[]
        managedSourcesFuse: (
            managedSources: ExternalDataSource[],
            availableSources: Record<string, SourceConfig> | null
        ) => Fuse<ExternalDataSource>
        filteredManagedSources: (
            managedSources: ExternalDataSource[],
            managedSourcesFuse: Fuse<ExternalDataSource>,
            managedSearchTerm: string
        ) => ExternalDataSource[]
        hasZendeskSource: (dataWarehouseSources: PaginatedResponse<ExternalDataSource> | null) => boolean
    }
}

export type sourceManagementLogicType = MakeLogicType<
    sourceManagementLogicValues,
    sourceManagementLogicActions,
    Record<string, any>,
    sourceManagementLogicMeta
>

export const sourceManagementLogic = kea<sourceManagementLogicType>([
    path(['products', 'dataWarehouse', 'sourceManagementLogic']),
    connect(() => ({
        values: [
            databaseTableListLogic,
            ['database', 'dataWarehouseTables'],
            sourcesDataLogic,
            ['dataWarehouseSources', 'dataWarehouseSourcesLoading'],
            availableSourcesLogic,
            ['availableSources'],
        ],
        actions: [
            databaseTableListLogic,
            ['loadDatabase'],
            joinsLogic,
            ['loadJoins'],
            sourcesDataLogic,
            [
                'loadSources',
                'loadSourcesSuccess',
                'loadSourcesFailure',
                'updateSource',
                'updateSourceRevenueAnalyticsConfig',
            ],
        ],
    })),
    actions({
        deleteSource: (source: ExternalDataSource) => ({ source }),
        reloadSource: (source: ExternalDataSource) => ({ source }),
        sourceLoadingFinished: (source: ExternalDataSource) => ({ source }),
        schemaLoadingFinished: (schema: ExternalDataSourceSchema) => ({ schema }),
        deleteSelfManagedTable: (tableId: string) => ({ tableId }),
        refreshSelfManagedTableSchema: (tableId: string) => ({ tableId }),
        setSearchTerm: (searchTerm: string) => ({ searchTerm }),
        setManagedSearchTerm: (managedSearchTerm: string) => ({ managedSearchTerm }),
        deleteJoin: (join: DataWarehouseViewLink) => ({ join }),
    }),
    loaders(({ actions, values }) => ({
        schemas: [
            null,
            {
                updateSchema: async (schema: ExternalDataSourceSchema) => {
                    // Optimistic UI updates before sending updates to the backend
                    const clonedSources = JSON.parse(
                        JSON.stringify(values.dataWarehouseSources?.results ?? [])
                    ) as ExternalDataSource[]
                    const sourceIndex = clonedSources.findIndex((n) => n.schemas?.find((m) => m.id === schema.id))
                    if (sourceIndex !== -1) {
                        const schemaIndex = clonedSources[sourceIndex].schemas.findIndex((n) => n.id === schema.id)
                        clonedSources[sourceIndex].schemas[schemaIndex] = schema

                        actions.loadSourcesSuccess({
                            ...values.dataWarehouseSources,
                            results: clonedSources,
                        })
                    }

                    await api.externalDataSchemas.update(schema.id, schema)
                    actions.loadSources()

                    return null
                },
            },
        ],
    })),
    reducers(() => ({
        sourceReloadingById: [
            {} as Record<string, boolean>,
            {
                reloadSource: (state, { source }) => ({
                    ...state,
                    [source.id]: true,
                }),
                deleteSource: (state, { source }) => ({
                    ...state,
                    [source.id]: true,
                }),
                sourceLoadingFinished: (state, { source }) => ({
                    ...state,
                    [source.id]: false,
                }),
            },
        ],
        schemaReloadingById: [
            {} as Record<string, boolean>,
            {
                schemaLoadingFinished: (state, { schema }) => ({
                    ...state,
                    [schema.id]: false,
                }),
            },
        ],
        searchTerm: [
            '' as string,
            {
                setSearchTerm: (_, { searchTerm }) => searchTerm,
            },
        ],
        managedSearchTerm: [
            '' as string,
            {
                setManagedSearchTerm: (_, { managedSearchTerm }) => managedSearchTerm,
            },
        ],
    })),
    selectors({
        selfManagedTables: [
            (s) => [s.dataWarehouseTables],
            (dataWarehouseTables: DatabaseSchemaDataWarehouseTable[]): DatabaseSchemaDataWarehouseTable[] => {
                return dataWarehouseTables.filter((table) => !table.source)
            },
        ],
        filteredSelfManagedTables: [
            (s) => [s.selfManagedTables, s.searchTerm],
            (
                selfManagedTables: DatabaseSchemaDataWarehouseTable[],
                searchTerm: string
            ): DatabaseSchemaDataWarehouseTable[] => {
                if (!searchTerm?.trim()) {
                    return selfManagedTables
                }
                const normalizedSearch = searchTerm.toLowerCase()
                return selfManagedTables.filter((table) => table.name.toLowerCase().includes(normalizedSearch))
            },
        ],
        managedSources: [
            (s) => [s.dataWarehouseSources],
            (
                dataWarehouseSources: null | import('lib/api').PaginatedResponse<ExternalDataSource>
            ): ExternalDataSource[] =>
                (dataWarehouseSources?.results ?? []).filter(
                    (source) => source.access_method?.toLowerCase() !== 'direct'
                ),
        ],
        // Search the display label as well as the internal source_type. Fuzzy, like the source catalog.
        managedSourcesFuse: [
            (s) => [s.managedSources, s.availableSources],
            (
                managedSources: ExternalDataSource[],
                availableSources: Record<string, import('~/queries/schema').SourceConfig> | null
            ): Fuse<ExternalDataSource> =>
                createFuse(managedSources, {
                    keys: [
                        {
                            name: 'label',
                            getFn: (source) => availableSources?.[source.source_type]?.label ?? source.source_type,
                        },
                        'source_type',
                        'prefix',
                        'description',
                    ],
                }),
        ],
        filteredManagedSources: [
            (s) => [s.managedSources, s.managedSourcesFuse, s.managedSearchTerm],
            (
                managedSources: ExternalDataSource[],
                managedSourcesFuse: Fuse<ExternalDataSource>,
                managedSearchTerm: string
            ): ExternalDataSource[] => {
                const trimmed = managedSearchTerm?.trim()
                if (!trimmed) {
                    return managedSources
                }
                return managedSourcesFuse.search(trimmed).map((r) => r.item)
            },
        ],
        hasZendeskSource: [
            (s) => [s.dataWarehouseSources],
            (dataWarehouseSources: null | import('lib/api').PaginatedResponse<ExternalDataSource>): boolean => {
                const sources = dataWarehouseSources?.results
                if (!sources) {
                    return false
                }

                return !!sources.some((source) => source?.source_type === 'Zendesk')
            },
        ],
    }),
    urlToAction(({ actions }) => ({
        '/data-warehouse/*': () => {
            actions.loadSources()
        },
    })),
    listeners(({ actions, values, cache }) => ({
        deleteSelfManagedTable: async ({ tableId }) => {
            await api.dataWarehouseTables.delete(tableId)
            actions.loadDatabase()
        },
        refreshSelfManagedTableSchema: async ({ tableId }) => {
            lemonToast.info('Updating schema...')
            await api.dataWarehouseTables.refreshSchema(tableId)
            lemonToast.success('Schema updated')
            actions.loadDatabase()
        },
        deleteSource: async ({ source }) => {
            await api.externalDataSources.delete(source.id)
            actions.loadSources()
            actions.sourceLoadingFinished(source)

            posthog.capture('source deleted', { sourceType: source.source_type })
        },
        reloadSource: async ({ source }) => {
            // Optimistic UI updates before sending updates to the backend
            const clonedSources = JSON.parse(
                JSON.stringify(values.dataWarehouseSources?.results ?? [])
            ) as ExternalDataSource[]
            const sourceIndex = clonedSources.findIndex((n) => n.id === source.id)
            clonedSources[sourceIndex].status = ExternalDataJobStatus.Running
            clonedSources[sourceIndex].schemas = clonedSources[sourceIndex].schemas.map((n) => {
                if (n.should_sync) {
                    return {
                        ...n,
                        status: ExternalDataSchemaStatus.Running,
                    }
                }

                return n
            })

            actions.loadSourcesSuccess({
                ...values.dataWarehouseSources,
                results: clonedSources,
            })

            try {
                await api.externalDataSources.reload(source.id)
                actions.loadSources()

                posthog.capture('source reloaded', { sourceType: source.source_type })
            } catch (e: any) {
                if (e.message) {
                    lemonToast.error(e.message)
                } else {
                    lemonToast.error('Cant refresh source at this time')
                }
            }
            actions.sourceLoadingFinished(source)
        },
        updateSchema: (schema) => {
            posthog.capture('schema updated', { shouldSync: schema.should_sync, syncType: schema.sync_type })
        },
        loadSourcesSuccess: () => {
            if (router.values.location.pathname.includes('data-warehouse')) {
                cache.disposables.add(() => {
                    const timerId = setTimeout(() => {
                        actions.loadSources()
                    }, REFRESH_INTERVAL)
                    return () => clearTimeout(timerId)
                }, 'refreshTimeout')
            }
        },
        loadSourcesFailure: () => {
            if (router.values.location.pathname.includes('data-warehouse')) {
                cache.disposables.add(() => {
                    const timerId = setTimeout(() => {
                        actions.loadSources()
                    }, REFRESH_INTERVAL)
                    return () => clearTimeout(timerId)
                }, 'refreshTimeout')
            }
        },
        deleteJoin: ({ join }): void => {
            void deleteWithUndo({
                endpoint: api.dataWarehouseViewLinks.determineDeleteEndpoint(),
                object: {
                    id: join.id,
                    name: `${join.field_name} on ${join.source_table_name}`,
                },
                callback: () => {
                    actions.loadDatabase()
                    actions.loadJoins()
                },
            }).catch((e) => {
                lemonToast.error(`Failed to delete warehouse view link: ${e.detail}`)
            })
        },
    })),
    afterMount(({ actions, values }) => {
        if (!values.database) {
            actions.loadDatabase()
        }
        actions.loadSources()
    }),
    beforeUnmount(() => {
        // Disposables plugin handles cleanup automatically
    }),
])
